import React from 'react';
import { compactAmount } from '../managementDashboard/DashboardCharts';
import { formatLocalizedNumber } from '../../../utils/numberFormatting';
import { measureValue, type PivotCells, type PivotMeasure } from './orderPivot';

interface Props {
  buckets: string[];
  cells: PivotCells;
  measure: PivotMeasure;
  bucketLabel: (bucket: string) => string;
  formatValue: (value: number | null) => string;
  language: string;
}

interface Bar {
  bucket: string;
  settled: number;
  forecast: number;
  /** Margin % and counts cannot be split: the whole bar, marked by what it holds. */
  whole: number | null;
  hasForecast: boolean;
  hasSettled: boolean;
}

const HEIGHT = 168;
const TOP = 12;
const BOTTOM = 26;
const LEFT = 56;
// Drawn at this width and scaled to the section; the height follows.
const WIDTH = 1080;

// Trend of one row of „Przekrój sprzedaży” across its period columns. Amounts
// stack the settled part (solid) under the forecast part (light, dashed
// outline), so the two layers never merge; margin % and order counts are
// one bar, solid only when nothing in it is forecast.
const PivotTrendChart: React.FC<Props> = ({ buckets, cells, measure, bucketLabel, formatValue, language }) => {
  const stacked = measure === 'revenue' || measure === 'margin' || measure === 'cost';
  const bars: Bar[] = buckets.map((bucket) => {
    const money = cells.byBucket.get(bucket);
    const settled = !money ? 0 : measure === 'revenue' ? money.invoiced : measure === 'cost' ? money.costActual : money.marginActual;
    const forecast = !money ? 0 : measure === 'revenue' ? money.waiting : measure === 'cost' ? money.cost - money.costActual : money.margin - money.marginActual;
    return {
      bucket,
      settled,
      forecast,
      whole: stacked ? null : measureValue(money, measure),
      hasForecast: !!money && money.waiting > 0,
      hasSettled: !!money && money.invoiced > 0,
    };
  });
  const tops = bars.map((bar) => (stacked ? Math.max(0, bar.settled) + Math.max(0, bar.forecast) : Math.max(0, bar.whole ?? 0)));
  const bottoms = bars.map((bar) => (stacked ? Math.min(0, bar.settled) + Math.min(0, bar.forecast) : Math.min(0, bar.whole ?? 0)));
  const max = Math.max(1, ...tops);
  const min = Math.min(0, ...bottoms);
  const width = WIDTH;
  const plot = HEIGHT - TOP - BOTTOM;
  const y = (value: number) => TOP + ((max - value) / (max - min)) * plot;
  const slot = (width - LEFT) / Math.max(1, bars.length);
  const barWidth = Math.min(36, slot * 0.62);
  const axis = (value: number) => (measure === 'marginPercent'
    ? `${formatLocalizedNumber(value, 0, language)}%`
    : measure === 'orders' ? formatLocalizedNumber(value, 0, language) : compactAmount(value, language));
  // Every label when they fit, else every second or third one.
  const labelEvery = slot >= 44 ? 1 : slot >= 24 ? 2 : 3;

  const rect = (x: number, from: number, to: number, className: string, key: string) => {
    const top = y(Math.max(from, to));
    const height = Math.max(0, y(Math.min(from, to)) - top);
    return height > 0 ? <rect key={key} className={className} x={x} y={top} width={barWidth} height={height} rx={3} /> : null;
  };

  return (
    <svg className="pivot-trend" viewBox={`0 0 ${width} ${HEIGHT}`} role="img">
      {[max, max / 2, 0, ...(min < 0 ? [min] : [])].map((value) => (
        <g key={`grid-${value}`}>
          <line className="pivot-trend__grid" x1={LEFT} x2={width} y1={y(value)} y2={y(value)} />
          <text className="pivot-trend__axis" x={LEFT - 8} y={y(value) + 4} textAnchor="end">{axis(value)}</text>
        </g>
      ))}
      {bars.map((bar, index) => {
        const x = LEFT + index * slot + (slot - barWidth) / 2;
        const value = stacked ? bar.settled + bar.forecast : bar.whole;
        return (
          <g key={bar.bucket}>
            <title>{`${bucketLabel(bar.bucket)}: ${formatValue(value)}`}</title>
            {stacked ? (
              <>
                {rect(x, 0, bar.settled, 'pivot-trend__bar is-settled', 's')}
                {rect(x, bar.settled, bar.settled + bar.forecast, 'pivot-trend__bar is-forecast', 'f')}
              </>
            ) : bar.whole != null && rect(x, 0, bar.whole, `pivot-trend__bar ${bar.hasForecast ? 'is-forecast' : 'is-settled'}`, 'w')}
            {index % labelEvery === 0 && (
              <text className="pivot-trend__axis" x={x + barWidth / 2} y={HEIGHT - 8} textAnchor="middle">{bucketLabel(bar.bucket)}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default PivotTrendChart;
